import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { clearError, closeNotification, fetchActiveNotifications, processActiveNotifications, snoozeNotification } from '../../store/slices/tripNotification';
import { format } from 'date-fns';
import {
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    Alert,
    Snackbar,
    TableCell,
    TableRow,
} from '@mui/material';
import {
    NotificationsContainer,
    StyledTableContainer,
    DescriptionCell,
    TripCodeCell,
    ActionsCell,
    NotificationRow,
    ActionsContainer,
    CategoryContainer,
    CategoryTitle,
    CategoryPaper,
    EmptyStateContainer,
} from './tripNotifications.styles';

interface TripNotificationsProps {
    onTripClick?: (tripId: string) => void;
    onTabChange?: (tabName: string) => void;
}

const TripNotifications: React.FC<TripNotificationsProps> = ({ onTripClick, onTabChange }) => {
    const dispatch = useDispatch<AppDispatch>();
    const notifications = useSelector((state: any) => state.tripNotifications.notifications);
    const error = useSelector((state: any) => state.tripNotifications.error);

    const categoryOrder = [
        'DRIVERNOTASSIGNED',
        'INPROGRESSNOTSET',
        'COMPLETEDNOTSET',
        'PENDINGTRIPACTION',
        'NOTCLOSED',
    ]
    
    const formatCategoryName = (category: string): string => {
        const categoryMap: Record<string, string> = {
            'PENDINGTRIPACTION': 'Pending Trip Action',
            'DRIVERNOTASSIGNED': 'Driver Not Assigned',
            'INPROGRESSNOTSET': 'In Progress Not Set',
            'COMPLETEDNOTSET': 'Completed Not Set',
            'NOTCLOSED': 'Not Closed',
        };

        return categoryMap[category] || category;
    };
    // Group notifications by category
    const groupedNotifications = React.useMemo(() => {
        const groups: Record<string, any[]> = {};
        notifications.forEach((notification: any) => {
            const category = notification.category || 'Uncategorized';
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(notification);
        });
        return groups;
    }, [notifications]);

    useEffect(() => {
        const fetchData = async () => {
            const currentDateTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
            try {
                await dispatch(processActiveNotifications(currentDateTime));
                await dispatch(fetchActiveNotifications(currentDateTime));
            }
            catch (error) {
                console.error('Error fetching or processing notifications:', error);
            }
        };
        fetchData();
    }, [dispatch]);

    const handleSnoozeClick = async (id: number) => {
        const currentDateTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
        try {
            await dispatch(snoozeNotification({ id, atDatetime: currentDateTime })).unwrap();
        } catch (error) {
            console.error('Error snoozing notification:', error);
        }
    };

    const handleCloseClick = async (id: number) => {
        try {
            await dispatch(closeNotification(id)).unwrap();
        } catch (error) {
            console.error('Error closing notification:', error);
        }
    };

    const handleCloseError = () => {
        dispatch(clearError());
    };

   return (
    <NotificationsContainer sx={{
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '10px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#1e1e1e',
            borderRadius: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#666',
            borderRadius: '5px',
            '&:hover': {
                backgroundColor: '#888',
            },
        },
        scrollBehavior: 'smooth',
        pr: 1,
    }}>
        <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                {error}
            </Alert>
        </Snackbar>

        {notifications.length === 0 ? (
            <Paper>
                <EmptyStateContainer>
                    <Typography variant="body1" color="textSecondary">
                        No notifications available
                    </Typography>
                </EmptyStateContainer>
            </Paper>
        ) : (
            <CategoryPaper>
                <StyledTableContainer>
                    <Table>
                        <TableBody>
                            {/* {Object.entries(groupedNotifications).map(([category, categoryNotifications]) => ( */}
                            {categoryOrder.filter(category => groupedNotifications[category]).map((category) => (
                                <React.Fragment key={category}>
                                    <TableRow>
                                        <TableCell colSpan={3} sx={{ padding: 0 }}>
                                            <CategoryTitle variant="h6">
                                                {formatCategoryName(category)} ({groupedNotifications[category].length})
                                            </CategoryTitle>
                                        </TableCell>
                                    </TableRow>
                                    {groupedNotifications[category].map((notification: any) => (
                                        <NotificationRow key={notification.id}>
                                            <TripCodeCell>
                                                {notification.tripCode}
                                            </TripCodeCell>
                                            <DescriptionCell>
                                                {notification.description}
                                            </DescriptionCell>
                                            <ActionsCell>
                                                <ActionsContainer>
                                                    <Button
                                                        color="warning"
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => handleSnoozeClick(notification.id)}
                                                        title="Snooze"
                                                    >
                                                        Snooze
                                                    </Button>
                                                    <Button
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleCloseClick(notification.id)}
                                                        title="Close"
                                                        variant="contained"
                                                    >
                                                        Close
                                                    </Button>
                                                </ActionsContainer>
                                            </ActionsCell>
                                        </NotificationRow>
                                    ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </CategoryPaper>
        )}
    </NotificationsContainer>
);
};

export default TripNotifications;